import React, { useState, useRef } from 'react';
import { Box, IconButton, Typography, Paper } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';

interface VideoPlayerProps {
  src: string;
  poster?: string;
  width?: string | number;
  height?: string | number;
  autoPlay?: boolean;
  controls?: boolean;
  muted?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  poster,
  width = '100%',
  height = 'auto',
  autoPlay = false,
  controls = true,
  muted = false
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleFullscreen = () => {
    const el = containerRef.current as any;
    if (!el) return;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen || el.mozRequestFullScreen;
    if (req) req.call(el);
  };

  // Native HTML5 video player (most stable for files on same origin)
  const renderNativeVideo = () => (
    <video
      width={width}
      height={height}
      controls={controls}
      autoPlay={autoPlay}
      muted={muted}
      poster={poster}
      playsInline
      preload="metadata"
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        height: 'auto',
        maxHeight: '70vh',
        borderRadius: '8px',
        pointerEvents: 'auto'
      }}
    >
      <source src={src} type="video/mp4" />
      <Typography variant="body2" color="error">
        Your browser does not support the video tag.
      </Typography>
    </video>
  );

  return (
    <Paper elevation={2} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
      <Box ref={containerRef} sx={{ width: '100%' }}>
        {renderNativeVideo()}
      </Box>
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <IconButton onClick={handleFullscreen} size="small" aria-label="full screen">
          <FullscreenIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default VideoPlayer;
